/**
 * Map Shelly API error codes to user-facing error keys (resolved at the i18n
 * layer in U5). Defaults to `generic_error` for anything unmapped.
 *
 * Error codes observed in shelly_api docs and shelly-app-3 handlers:
 *   - wrong_credentials      (auth + oauth)
 *   - max_attempts           (auth)
 *   - unauthorized_client    (oauth)
 *   - invalid_unit           (oauth, when picked unit isn't in pkey.id)
 *   - login_failed           (oauth, server-side)
 *   - no_connection          (synthesized client-side on fetch failure)
 *   - server_error           (synthesized client-side on 5xx)
 */
export type ErrorCode =
	| 'wrong_credentials'
	| 'max_attempts'
	| 'unauthorized_client'
	| 'invalid_unit'
	| 'login_failed'
	| 'no_connection'
	| 'server_error'
	| 'generic_error';

const KNOWN_CODES: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
	'wrong_credentials',
	'max_attempts',
	'unauthorized_client',
	'invalid_unit',
	'login_failed',
	'no_connection',
	'server_error',
	'generic_error',
]);

/**
 * Extract the first error code from a Shelly API error response.
 * The wire shape is `{ isok: false, errors: { <code>: <message>, ... } }`.
 */
export function extractErrorCode(body: unknown): ErrorCode {
	if (body && typeof body === 'object' && 'errors' in body) {
		const errors = (body as { errors: unknown }).errors;
		if (errors && typeof errors === 'object') {
			const keys = Object.keys(errors as Record<string, unknown>);
			if (keys.length > 0) return normalize(keys[0]);
		}
	}
	return 'generic_error';
}

/**
 * Translate an HTTP / network failure into an ErrorCode.
 * Treat 5xx as server_error, network/CORS failures as no_connection.
 */
export function errorCodeFromHttp(
	status: number | undefined,
	cause: 'response' | 'network',
): ErrorCode {
	if (cause === 'network') return 'no_connection';
	if (status !== undefined && status >= 500) return 'server_error';
	return 'generic_error';
}

/**
 * i18n key used by the UI to look up the localized message.
 * Lookup table is owned by `lib/i18n.ts` in U5; this just gives the key.
 */
export function i18nKeyForError(code: ErrorCode): string {
	return `error_${code}`;
}

/**
 * Errors the user can't fix from inside the consent / picker modal. When
 * /oauth/login fails with one of these from the consent step, we abandon
 * the in-page flow and redirect to the third party's `redirect_uri` with
 * an OAuth-spec `error` param so their handler can show its own retry UI.
 *
 *   - unauthorized_client: bad client_id / not in OAUTH_CLIENTS allowlist.
 *     The user can't re-authenticate their way out of it.
 *   - server_error / login_failed: 5xx / server-side failure. Retrying
 *     in-page just hits the same server. The third party can offer the
 *     user a "try again later" with the assistant's own affordances.
 *
 * `wrong_credentials`, `max_attempts`, `invalid_unit`, `no_connection`
 * stay in-modal because the user CAN fix them (re-enter password, pick a
 * different home, reconnect to wifi).
 */
const NON_RECOVERABLE: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
	'unauthorized_client',
	'server_error',
	'login_failed',
]);

export function isNonRecoverable(code: ErrorCode): boolean {
	return NON_RECOVERABLE.has(code);
}

/**
 * Map an internal ErrorCode to the OAuth 2.0 §4.1.2.1 error-response code
 * we should put in the `error` query param when redirecting back to the
 * third party. We use a small mapping rather than passing the internal
 * code through verbatim because Google's handlers only recognize the
 * standard set.
 */
export function oauthErrorCodeFor(code: ErrorCode): string {
	switch (code) {
		case 'unauthorized_client':
			return 'unauthorized_client';
		case 'server_error':
		case 'login_failed':
		default:
			return 'server_error';
	}
}

function normalize(raw: string): ErrorCode {
	const k = raw as ErrorCode;
	return KNOWN_CODES.has(k) ? k : 'generic_error';
}
